// src/runs/useRunMonitor.js
//
// Watches surveillance runs queued on Deacon. POST /run-surveillance and
// /run-book only enqueue a run (202 + load_id); a Beacon Bob job picks it up
// and the pipeline records REQUESTED -> IN PROGRESS -> COMPLETED / FAILED
// under that load_id. This hook polls GET /run-status/<load_id> every 15 s
// until the run finishes, then calls `onRunFinished` and toasts the outcome.
//
// One poller per load_id (a setTimeout chain, so polls never overlap). Runs
// still REQUESTED / IN PROGRESS in /run-history are resumed after a reload via
// `resumeFromHistory`. Everything stops on unmount.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotification } from '@1823-partners/core';
import { getRunStatus } from '../api';

export const RUN_STATUS_POLL_MS = 15000;
// Mirrors the backend's 409 window: an unfinished run whose last event is
// older than this is treated as abandoned, not as blocking or worth polling.
export const ACTIVE_RUN_STALE_MS = 6 * 60 * 60 * 1000;
// Consecutive failed polls before one (and only one) warning toast.
export const POLL_FAILURES_BEFORE_WARNING = 3;

export const RUN_TYPE_MONTHLY = 'Monthly';
export const RUN_TYPE_BOOK = 'Book';

const ACTIVE_STATUSES = new Set(['REQUESTED', 'IN PROGRESS']);
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED']);

export const normalizeStatus = (status) =>
  String(status ?? '').trim().toUpperCase().replace(/_/g, ' ');

export const isActiveStatus = (status) => ACTIVE_STATUSES.has(normalizeStatus(status));
export const isTerminalStatus = (status) => TERMINAL_STATUSES.has(normalizeStatus(status));

export const sameRunType = (a, b) =>
  String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

// A run's COB date as YYYY-MM-DD (the API may send a date, an ISO string or an
// HTTP date); null when absent or unparseable.
export const toRunDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
};

// Backend timestamps are UTC (tagged 'Z'); a zone-less one is read as UTC too.
const parseUtcMillis = (value) => {
  if (!value) return null;
  let text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text)) {
    text = `${text.replace(' ', 'T')}Z`;
  }
  const millis = Date.parse(text);
  return Number.isNaN(millis) ? null : millis;
};

// Unknown timestamps are never stale (they cannot be judged).
export const isStaleRun = (modifiedDate, now = Date.now()) => {
  const millis = parseUtcMillis(modifiedDate);
  return millis !== null && now - millis > ACTIVE_RUN_STALE_MS;
};

const toAccounts = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((a) => a.trim()).filter(Boolean);
  }
  return [];
};

const pick = (value, fallback) => (value === undefined || value === null || value === '' ? fallback : value);

// Merge an API/history payload over what we already know about a run.
const toRun = (source, known = {}) => ({
  load_id: String(pick(source.load_id, known.load_id)),
  status: normalizeStatus(pick(source.status, known.status)),
  run_type: pick(source.run_type, known.run_type ?? null),
  run_date: pick(toRunDate(source.run_date), known.run_date ?? null),
  accounts: source.accounts !== undefined && source.accounts !== null
    ? toAccounts(source.accounts)
    : (known.accounts || []),
  notes: pick(source.notes, known.notes ?? null),
  modified_date: pick(source.modified_date, known.modified_date ?? null),
});

export const describeRun = (run) => {
  const kind = sameRunType(run.run_type, RUN_TYPE_BOOK) ? 'Run book' : 'Surveillance run';
  return `${kind} for ${run.run_date || 'an unknown date'} (load ${run.load_id})`;
};

export function useRunMonitor({ onRunFinished } = {}) {
  const { showNotification } = useNotification();
  const [activeRuns, setActiveRuns] = useState(() => new Map());

  const runsRef = useRef(new Map()); // load_id -> run (the pollers' source of truth)
  const pollersRef = useRef(new Map()); // load_id -> timeout id, or null while a poll is in flight
  const failuresRef = useRef(new Map()); // load_id -> consecutive failed polls
  const trackedAtRef = useRef(new Map()); // load_id -> when we started watching it (ms)
  const finishedRef = useRef(new Set()); // load_ids already seen COMPLETED / FAILED
  const mountedRef = useRef(false);
  const pollRef = useRef(null);
  const onFinishedRef = useRef(onRunFinished);
  const notifyRef = useRef(showNotification);
  onFinishedRef.current = onRunFinished;
  notifyRef.current = showNotification;

  const publish = useCallback(() => {
    if (mountedRef.current) setActiveRuns(new Map(runsRef.current));
  }, []);

  const stop = useCallback((loadId) => {
    const timer = pollersRef.current.get(loadId);
    if (timer) clearTimeout(timer);
    pollersRef.current.delete(loadId);
    failuresRef.current.delete(loadId);
    trackedAtRef.current.delete(loadId);
    runsRef.current.delete(loadId);
  }, []);

  // Idle for over 6 h, judged by the run's last event, or by when we started
  // watching it when no event time is known (e.g. /run-status keeps failing).
  const isIdle = useCallback((run) => {
    const since = run?.modified_date
      || new Date(trackedAtRef.current.get(run?.load_id) ?? Date.now()).toISOString();
    return isStaleRun(since);
  }, []);

  const schedule = useCallback((loadId) => {
    if (!mountedRef.current || !runsRef.current.has(loadId)) return;
    const previous = pollersRef.current.get(loadId);
    if (previous) clearTimeout(previous);
    pollersRef.current.set(loadId, setTimeout(() => pollRef.current(loadId), RUN_STATUS_POLL_MS));
  }, []);

  // Still wanted after an await: mounted, tracked, and not stopped meanwhile.
  const stillPolling = (loadId) =>
    mountedRef.current && runsRef.current.has(loadId) && pollersRef.current.has(loadId);

  pollRef.current = async (loadId) => {
    if (!stillPolling(loadId)) return;
    pollersRef.current.set(loadId, null); // in flight: track() must not start a second poller
    let data;
    try {
      const response = await getRunStatus(loadId);
      data = response?.data && typeof response.data === 'object' ? response.data : {};
    } catch (error) {
      if (!stillPolling(loadId)) return;
      if (error?.response?.status === 404) {
        // The backend does not know this load_id; there is nothing to watch.
        console.warn(`Run ${loadId} is unknown to /run-status; no longer monitoring it.`);
        stop(loadId);
        publish();
        return;
      }
      const known = runsRef.current.get(loadId);
      if (isIdle(known)) {
        // Failing for longer than a run can stay open: give up, once.
        console.warn(`Run ${loadId} could not be checked for over 6 hours; no longer monitoring it.`);
        notifyRef.current(
          `Stopped checking ${describeRun(known)}; see Run History for its status.`,
          'warning',
        );
        stop(loadId);
        publish();
        return;
      }
      // Transient: keep polling, warn once.
      const failures = (failuresRef.current.get(loadId) || 0) + 1;
      failuresRef.current.set(loadId, failures);
      console.warn(`Polling run ${loadId} failed (${failures} in a row); retrying.`, error);
      if (failures === POLL_FAILURES_BEFORE_WARNING) {
        notifyRef.current(
          `Could not check the status of ${describeRun(runsRef.current.get(loadId))}. Still retrying.`,
          'warning',
        );
      }
      schedule(loadId);
      return;
    }
    if (!stillPolling(loadId)) return;
    failuresRef.current.delete(loadId);
    const previous = runsRef.current.get(loadId);
    const run = toRun(data, previous);

    if (isTerminalStatus(run.status)) {
      stop(loadId);
      finishedRef.current.add(loadId);
      publish();
      if (run.status === 'COMPLETED') {
        notifyRef.current(`${describeRun(run)} completed.`, 'success');
      } else {
        notifyRef.current(`${describeRun(run)} failed${run.notes ? `: ${run.notes}` : '.'}`, 'error');
      }
      onFinishedRef.current?.(run);
      return;
    }
    if (isIdle(run)) {
      console.warn(`Run ${loadId} has not moved in over 6 hours; no longer monitoring it.`);
      stop(loadId);
      publish();
      return;
    }
    runsRef.current.set(loadId, run);
    if (run.status !== previous.status) publish();
    schedule(loadId);
  };

  // Start (or keep) monitoring an unfinished run. Never starts a second
  // poller for a load_id. Returns true when the run is being monitored.
  const track = useCallback((input) => {
    const loadId = input?.load_id;
    if (loadId === undefined || loadId === null || loadId === '') return false;
    const key = String(loadId);
    if (finishedRef.current.has(key)) return false;
    if (runsRef.current.has(key)) return true; // its poller is the authority now
    const run = toRun({ ...input, status: pick(input.status, 'REQUESTED') });
    if (!isActiveStatus(run.status) || isStaleRun(run.modified_date)) return false;
    runsRef.current.set(key, run);
    trackedAtRef.current.set(key, Date.now());
    if (!pollersRef.current.has(key)) schedule(key);
    publish();
    return true;
  }, [publish, schedule]);

  // A 202 from /run-surveillance or /run-book.
  const runRequested = useCallback((payload) => {
    if (!track(payload)) return;
    const run = runsRef.current.get(String(payload.load_id));
    const later = payload.kicked === false
      ? ' It will start when the run job next checks the queue.'
      : '';
    notifyRef.current(`Run requested: ${describeRun(run)}.${later}`, 'info');
  }, [track]);

  // Rows from /run-history (keys upper-cased by RunHistory).
  const resumeFromHistory = useCallback((rows) => {
    (rows || []).forEach((row) => {
      if (!row || !isActiveStatus(row.STATUS)) return;
      track({
        load_id: row.LOAD_ID,
        status: row.STATUS,
        run_type: row.RUN_TYPE,
        run_date: row.RUN_DATE,
        accounts: row.ACCOUNTS,
        notes: row.NOTES,
        modified_date: row.MODIFIED_DATE,
      });
    });
  }, [track]);

  const isRunActive = useCallback((runType, runDate) => {
    const date = toRunDate(runDate);
    if (!date) return false;
    for (const run of activeRuns.values()) {
      if (sameRunType(run.run_type, runType) && run.run_date === date) return true;
    }
    return false;
  }, [activeRuns]);

  useEffect(() => {
    mountedRef.current = true;
    // StrictMode unmounts and remounts once: restart pollers for runs kept.
    runsRef.current.forEach((_run, loadId) => {
      if (!pollersRef.current.has(loadId)) schedule(loadId);
    });
    const pollers = pollersRef.current;
    return () => {
      mountedRef.current = false;
      pollers.forEach((timer) => { if (timer) clearTimeout(timer); });
      pollers.clear();
    };
  }, [schedule]);

  return { activeRuns, track, runRequested, resumeFromHistory, isRunActive };
}

export default useRunMonitor;
